import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useClient } from 'jsonapi-react';
import moment from 'moment';
import toast from 'react-hot-toast';
import { FiChevronDown, FiChevronLeft } from 'react-icons/fi';
import usePermission from '@/hooks/usePermission';
import { useAuthorityLevels } from '@/hooks/useAuthorityLevel';
import { Error404 } from '@/components/custom/error';
import { downloadReportFile } from '@/utils/downloadReportFile';
import { UsageLineChart } from './charts/UsageLineChart';
import { GreDistributionChart } from './charts/GreDistributionChart';
import { SubjectPerformanceChart } from './charts/SubjectPerformanceChart';
import { TeachersTable } from './TeachersTable';
import { SectionState } from './SectionState';
import { useReportSection } from './useReportSection';
import { buildDateQuery, buildUsageChartData, mapGreDistribution, mapSubjectDistribution, mapTeachers, TEACHERS_PER_PAGE } from './reportApi';
import { formatInteger } from './formatters';

const BASE_PATH = '/reports/live_challenges';

export function LiveChallengeReport() {
  const navigate = useNavigate();
  const client = useClient();
  const canDownload = usePermission('Download de relatórios', 'Relatórios');
  const authorityLevels = useAuthorityLevels();
  const isAdmin = authorityLevels.includes('admin');

  // A navegação de metricas/institucional pra cá (ou de volta do relatório do
  // professor) é client-side (pushState) e o navegador não reseta o scroll
  // sozinho nessa transição — só num reload de página. `behavior: 'instant'`
  // é necessário pq até a atribuição direta em scrollTop respeita o
  // `scroll-behavior: smooth` global (App.css); só passando 'instant'
  // explicitamente essa chamada ignora o CSS e abre a tela já no topo, sem
  // precisar mexer em nada global.
  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, []);

  // Datas ficam na URL (?start_date=&end_date=) em vez de em estado local
  // pra sobreviver à navegação entre este relatório e o do professor
  // (TEACHER_LIVE_CHALLENGE), nos dois sentidos — ver TeachersTable e
  // TeacherReport.jsx.
  const [searchParams, setSearchParams] = useSearchParams();
  const startDate = searchParams.get('start_date') || moment().subtract(14, 'days').format('YYYY-MM-DD');
  const endDate = searchParams.get('end_date') || moment().format('YYYY-MM-DD');

  const setStartDate = value => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.set('start_date', value);
        return next;
      },
      { replace: true }
    );
  };

  const setEndDate = value => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.set('end_date', value);
        return next;
      },
      { replace: true }
    );
  };

  const [greSortDirection, setGreSortDirection] = useState('desc');

  const [teacherSearch, setTeacherSearch] = useState('');
  const [debouncedTeacherSearch, setDebouncedTeacherSearch] = useState('');
  const [teacherSortDirection, setTeacherSortDirection] = useState('desc');

  // Lista de professores usa infinite scroll (mesmo padrão de
  // RegistrationsTable/LiveClassesTable em GeneralRegistrationReport), não
  // paginação numerada.
  const [teachers, setTeachers] = useState([]);
  const [teacherPage, setTeacherPage] = useState(1);
  const [teachersHasMore, setTeachersHasMore] = useState(true);
  const [teachersInitialLoading, setTeachersInitialLoading] = useState(true);

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedTeacherSearch(teacherSearch.trim()), 300);
    return () => clearTimeout(timeout);
  }, [teacherSearch]);

  const dateQuery = buildDateQuery(startDate, endDate);

  const loadTeachers = async (page, replace) => {
    const params = new URLSearchParams({ ...dateQuery, order: teacherSortDirection, 'page[number]': page, 'page[size]': TEACHERS_PER_PAGE });
    if (debouncedTeacherSearch) params.set('q', debouncedTeacherSearch);

    const { data, error, meta } = await client.fetch(`${BASE_PATH}/teachers?${params}`);

    if (error) {
      toast.error('Erro ao buscar os professores.');
    } else {
      const mapped = mapTeachers(data.teachers);
      setTeachers(prev => (replace ? mapped : [...prev, ...mapped]));
      const pagination = meta?.pagination;
      setTeachersHasMore(pagination ? pagination.current_page < pagination.total_pages : false);
    }

    if (replace) setTeachersInitialLoading(false);
  };

  useEffect(() => {
    setTeachers([]);
    setTeacherPage(1);
    setTeachersHasMore(true);
    setTeachersInitialLoading(true);
    loadTeachers(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, teacherSortDirection, debouncedTeacherSearch]);

  useEffect(() => {
    if (teacherPage > 1) {
      loadTeachers(teacherPage, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teacherPage]);

  const kpisSection = useReportSection(() => client.fetch(`${BASE_PATH}/kpis?${new URLSearchParams(dateQuery)}`), [startDate, endDate]);
  const usageSection = useReportSection(() => client.fetch(`${BASE_PATH}/usage_chart?${new URLSearchParams(dateQuery)}`), [startDate, endDate]);
  const greSection = useReportSection(() => client.fetch(`${BASE_PATH}/gre_distribution?${new URLSearchParams({ ...dateQuery, order: greSortDirection })}`), [startDate, endDate, greSortDirection]);
  const subjectSection = useReportSection(() => client.fetch(`${BASE_PATH}/subject_distribution?${new URLSearchParams(dateQuery)}`), [startDate, endDate]);

  const kpis = [
    { id: 'executed', title: 'Desafios realizados', value: kpisSection.data?.['finished-activations'], variation: 'report-kpi-card--lightning' },
    { id: 'participants', title: 'Média de participantes', value: kpisSection.data?.['average-participants'], variation: 'report-kpi-card--users' },
    { id: 'teachers', title: 'Professores que utilizaram', value: kpisSection.data?.['active-teachers'], variation: 'report-kpi-card--teachers' }
  ];

  const usage = useMemo(() => (usageSection.data ? buildUsageChartData(usageSection.data['chart-data'], { startDate, endDate }) : null), [usageSection.data, startDate, endDate]);
  const greDistribution = useMemo(() => (greSection.data ? mapGreDistribution(greSection.data.distribution) : []), [greSection.data]);
  const subjectPerformance = useMemo(() => (subjectSection.data ? mapSubjectDistribution(subjectSection.data.distribution) : []), [subjectSection.data]);

  const handleExport = async () => {
    try {
      await downloadReportFile(`${BASE_PATH}/teachers.xlsx`, dateQuery);
    } catch (error) {
      toast.error('Erro ao baixar o relatório');
    }
  };

  if (!isAdmin) {
    return <Error404 />;
  }

  return (
    <div className="report">
      <header className="report__header">
        <h1 className="report__title">
          <button
            className="card__icon-wrapper"
            aria-label="Voltar"
            title="Voltar"
            onClick={() => navigate('/metricas/institucional')}
          >
            <FiChevronLeft className="card__icon" />
          </button>
          Desafio Ao Vivo
        </h1>
        <p className="report__description">Acompanhe a participação dos estudantes nas disputas em tempo real</p>
      </header>

      <main className="report__body">
        <div className="report__filter">
          <div className="report-form__datepicker">
            <input
              className="report-form__control"
              id="start-date"
              name="start-date"
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              title="Data de início"
              aria-label="Data de início"
            />
            <input
              className="report-form__control"
              id="end-date"
              name="end-date"
              type="date"
              value={endDate}
              min={startDate}
              onChange={e => setEndDate(e.target.value)}
              title="Data de fim"
              aria-label="Data de fim"
            />
          </div>
        </div>

        <div className="report-widget">
          <div className="report-widget__header">
            <h3 className="report-widget__title">Visão geral</h3>
          </div>
          <div className="report-widget__body">
            <SectionState
              loading={kpisSection.loading}
              error={kpisSection.error}
              onRetry={kpisSection.reload}
              errorTitle="Não foi possível carregar os indicadores"
            >
              <div className="report-kpi-card-wrapper">
                {kpis.map(kpi => (
                  <div
                    className={`report-kpi-card ${kpi.variation}`}
                    key={kpi.id}
                  >
                    <h3 className="report-kpi-card__title">{kpi.title}</h3>
                    <p className="report-kpi-card__value">{formatInteger(kpi.value)}</p>
                  </div>
                ))}
              </div>
            </SectionState>
          </div>
        </div>

        <div className="report-widget">
          <div className="report-widget__header">
            <div className="report-widget__inner">
              <h3 className="report-widget__title">Uso ao longo do tempo</h3>
              <p className="report-widget__subtitle">Quantidade de desafios criados ao longo do período selecionado</p>
            </div>
          </div>
          <div className="report-widget__body">
            <SectionState
              loading={usageSection.loading}
              error={usageSection.error}
              onRetry={usageSection.reload}
              errorTitle="Não foi possível carregar o gráfico de uso"
            >
              <UsageLineChart
                categories={usage?.categories}
                series={usage?.series}
              />
            </SectionState>
          </div>
        </div>

        <div className="report-widget">
          <div className="report-widget__header">
            <h3 className="report-widget__title">Desafios ativados por GRE</h3>
            <button
              type="button"
              className="btn btn--none flex items-center gap-1 text-xs font-semibold text-neutral-darker"
              onClick={() => setGreSortDirection(direction => (direction === 'asc' ? 'desc' : 'asc'))}
            >
              Ordenar pelo {greSortDirection === 'asc' ? 'maior' : 'menor'}
              <FiChevronDown style={{ transform: greSortDirection === 'asc' ? 'rotate(180deg)' : 'rotate(0deg)' }} />
            </button>
          </div>
          <div className="report-widget__body">
            <SectionState
              loading={greSection.loading}
              error={greSection.error}
              onRetry={greSection.reload}
              errorTitle="Não foi possível carregar a distribuição por GRE"
            >
              <GreDistributionChart data={greDistribution} />
            </SectionState>
          </div>
        </div>

        <div className="report-widget">
          <div className="report-widget__header">
            <div className="report-widget__inner">
              <h3 className="report-widget__title">Ativações por componente curricular x desempenho dos alunos</h3>
              <p className="report-widget__subtitle">Barra maiores mostram a quantidade de ativações. Dentro dela, a divisão entre acertos e erros.</p>
            </div>
          </div>
          <div className="report-widget__body">
            <SectionState
              loading={subjectSection.loading}
              error={subjectSection.error}
              onRetry={subjectSection.reload}
              errorTitle="Não foi possível carregar o desempenho por componente curricular"
            >
              <SubjectPerformanceChart data={subjectPerformance} />
            </SectionState>
          </div>
        </div>

        <div className="report-widget">
          <div className="report-widget__header">
            <h3 className="report-widget__title">Professores que ativaram desafios</h3>
            {canDownload && (
              <button
                type="button"
                className="btn btn--outline"
                onClick={handleExport}
              >
                Exportar relatório
              </button>
            )}
          </div>
          <div className="report-widget__body">
            <TeachersTable
              teachers={teachers}
              dateQueryString={searchParams.toString()}
              search={teacherSearch}
              onSearchChange={setTeacherSearch}
              sortDirection={teacherSortDirection}
              onSortToggle={() => setTeacherSortDirection(direction => (direction === 'asc' ? 'desc' : 'asc'))}
              hasMore={teachersHasMore}
              onLoadMore={() => setTeacherPage(page => page + 1)}
              initialLoading={teachersInitialLoading}
            />
          </div>
        </div>
      </main>
    </div>
  );
}
